import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Mic, Receipt, Plus, Loader2, Upload } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { useAccount } from '@/contexts/AccountContext';
import { analyzeReceipt, transcribeAudio, extractTransactionDetails } from '@/lib/openai';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TransactionInputProps {
  onSuccess?: () => void;
}

interface Wallet {
  id: string;
  name: string;
  balance: number;
  type: string;
  is_active: boolean;
}

const CATEGORIES = [
  'Food & Dining',
  'Transportation',
  'Shopping',
  'Bills & Utilities',
  'Entertainment',
  'Health & Medical',
  'Education',
  'Travel',
  'Gifts & Donations',
  'Other'
];

interface ExtractedTransaction {
  amount: number;
  category: string;
  date: string;
  description: string;
  type: 'income' | 'expense';
}

const TransactionInput = ({ onSuccess }: TransactionInputProps) => {
  const { toast } = useToast();
  const { accountType } = useAccount();
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [date, setDate] = useState<Date>(new Date());
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'income' | 'expense'>('expense');
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [transactionType, setTransactionType] = useState<'income' | 'expense'>('expense');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [extractedTransactions, setExtractedTransactions] = useState<ExtractedTransaction[]>([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [currentTransactionIndex, setCurrentTransactionIndex] = useState(0);
  const [receiptTransactions, setReceiptTransactions] = useState<ExtractedTransaction[]>([]);
  const [currentReceiptIndex, setCurrentReceiptIndex] = useState(0);
  const [showReceiptConfirmationDialog, setShowReceiptConfirmationDialog] = useState(false);

  const fetchWallets = async () => {
    try {
      console.log('Fetching wallets...');
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.error('No user found');
        return;
      }

      const { data: walletsData, error } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('name');
      
      if (error) {
        console.error('Error fetching wallets:', error);
        throw error;
      }

      console.log('Fetched wallets:', walletsData);
      setWallets(walletsData || []);
      
      // Set first wallet as default if available
      if (walletsData && walletsData.length > 0) {
        setSelectedWallet(walletsData[0]);
      }
    } catch (error) {
      console.error('Error in fetchWallets:', error);
      toast({
        title: "Error",
        description: "Failed to load wallets",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    console.log('Component mounted, fetching wallets...');
    fetchWallets();
  }, []);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || !category || !selectedWallet) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('No user found');

      // Get the selected wallet to determine the type
      const selectedWalletData = wallets.find(w => w.id === selectedWallet.id);
      if (!selectedWalletData) throw new Error('Selected wallet not found');

      const parsedAmount = parseFloat(amount);
      // Calculate new balance
      const newBalance = type === 'income' 
        ? selectedWallet.balance + parsedAmount
        : selectedWallet.balance - parsedAmount;

      console.log(`Transaction: ${type}, Amount: ${parsedAmount}, Old balance: ${selectedWallet.balance}, New balance: ${newBalance}`);

      // Start a transaction
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .insert({
          user_id: user.id,
          wallet_id: selectedWallet.id,
          amount: parsedAmount,
          category,
          transaction_date: date.toISOString(),
          description,
          account_type: accountType,
          type: type
        })
        .select()
        .single();

      if (transactionError) throw transactionError;

      // Update wallet balance directly instead of using RPC
      const { error: walletError } = await supabase
        .from('wallets')
        .update({ balance: newBalance })
        .eq('id', selectedWallet.id);

      if (walletError) throw walletError;

      toast({
        title: "Success",
        description: "Transaction added successfully!",
      });

      // Reset form
      setAmount('');
      setCategory('');
      setDescription('');
      setDate(new Date());

      // Reload wallets to update balances
      fetchWallets();

      onSuccess?.();
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add transaction. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const processReceipt = async () => {
    if (!receiptFile) {
      toast({
        title: "Error",
        description: "Please select a receipt image first.",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const base64String = e.target?.result as string;
          const base64Data = base64String.split(',')[1];
          
          console.log('Processing receipt image...');
          const result = await analyzeReceipt(base64Data);
          console.log('Receipt analysis result:', result);
          
          // Create a transaction object with the selected type
          const transaction: ExtractedTransaction = {
            amount: result.amount,
            category: result.category || 'Other',
            date: result.date || new Date().toISOString(),
            description: `At ${result.place}. Items: ${result.items.join(', ')}`,
            type: transactionType // Use the selected type for all transactions
          };

          // Add to receipt transactions array
          setReceiptTransactions(prev => [...prev, transaction]);
          
          // Show confirmation dialog
          setShowReceiptConfirmationDialog(true);
          
          // Reset receipt file
          setReceiptFile(null);
          
          toast({
            title: "Success",
            description: "Receipt processed successfully! You can add more receipts or confirm the transactions.",
          });
        } catch (error) {
          console.error('Error processing receipt:', error);
          toast({
            title: "Error",
            description: error instanceof Error ? error.message : "Failed to process receipt. Please try again.",
            variant: "destructive",
          });
        }
      };

      reader.onerror = () => {
        toast({
          title: "Error",
          description: "Failed to read the receipt file. Please try again.",
          variant: "destructive",
        });
      };

      reader.readAsDataURL(receiptFile);
    } catch (error) {
      console.error('Error processing receipt:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process receipt. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const startRecording = async () => {
    if (!selectedWallet) {
      toast({
        title: "Error",
        description: "Please select a wallet first before recording.",
        variant: "destructive",
      });
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        try {
          setProcessing(true);
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          console.log('Processing audio recording...');
          const transcribedText = await transcribeAudio(audioBlob);
          console.log('Transcribed text:', transcribedText);
          const result = await extractTransactionDetails(transcribedText);
          console.log('Transaction details:', result);
          
          // Handle multiple transactions
          const transactions = result.transactions || [];
          setExtractedTransactions(transactions);
          
          if (transactions.length > 0) {
            // Show confirmation dialog
            setShowConfirmationDialog(true);
          }
          
          toast({
            title: "Success",
            description: `Found ${transactions.length} transaction(s). Please review and confirm.`,
          });
        } catch (error) {
          console.error('Error processing voice input:', error);
          toast({
            title: "Error",
            description: "Failed to process voice input. Please try again.",
            variant: "destructive",
          });
        } finally {
          setProcessing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast({
        title: "Recording Started",
        description: "Click the button again to stop recording.",
      });
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to access microphone. Please check permissions.",
        variant: "destructive",
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      setHasRecording(true);
      toast({
        title: "Recording Stopped",
        description: "Click 'Analyze Recording' to process your voice input.",
      });
    }
  };

  const analyzeRecording = async () => {
    if (!selectedWallet) {
      toast({
        title: "Error",
        description: "Please select a wallet first before analyzing the recording.",
        variant: "destructive",
      });
      return;
    }

    if (!hasRecording || audioChunksRef.current.length === 0) {
      toast({
        title: "Error",
        description: "No recording available to analyze.",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
      console.log('Processing audio recording...');
      const transcribedText = await transcribeAudio(audioBlob);
      console.log('Transcribed text:', transcribedText);
      const results = await extractTransactionDetails(transcribedText);
      console.log('Transaction details:', results);
      
      // Handle multiple transactions
      const transactions = results.transactions || [];
      setExtractedTransactions(transactions);
      
      if (transactions.length > 0) {
        // Pre-fill the form with the first transaction
        const firstTransaction = transactions[0];
        setAmount(firstTransaction.amount.toString());
        setCategory(firstTransaction.category);
        const parsedDate = firstTransaction.date ? new Date(firstTransaction.date) : new Date();
        setDate(parsedDate);
        setDescription(firstTransaction.description);
        setType(firstTransaction.type);
        
        // Show confirmation dialog
        setShowConfirmationDialog(true);
      }
      
      toast({
        title: "Success",
        description: `Found ${transactions.length} transaction(s). Please review and confirm.`,
      });

      // Reset recording state
      setHasRecording(false);
      audioChunksRef.current = [];
    } catch (error) {
      console.error('Error processing voice input:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to process voice input. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleConfirmTransaction = async (transactionsToConfirm: ExtractedTransaction[]) => {
    if (!selectedWallet || !transactionsToConfirm.length) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Process the transactions
      for (const transaction of transactionsToConfirm) {
        const transactionDate = transaction.date ? new Date(transaction.date) : new Date();
        
        const { data: transactionData, error } = await supabase
          .from('transactions')
          .insert([
            {
              user_id: user.id,
              wallet_id: selectedWallet.id,
              amount: transaction.amount,
              category: transaction.category,
              transaction_date: transactionDate.toISOString(),
              description: transaction.description,
              type: transaction.type,
              account_type: accountType
            }
          ])
          .select()
          .single();

        if (error) throw error;

        // Update wallet balance directly instead of using RPC
        const newBalance = transaction.type === 'income' 
          ? selectedWallet.balance + transaction.amount
          : selectedWallet.balance - transaction.amount;

        const { error: walletError } = await supabase
          .from('wallets')
          .update({ balance: newBalance })
          .eq('id', selectedWallet.id);

        if (walletError) throw walletError;

        // Update local state
        setWallets(wallets.map(wallet => 
          wallet.id === selectedWallet.id 
            ? { ...wallet, balance: newBalance }
            : wallet
        ));
      }

      // Remove confirmed transactions from the list
      setExtractedTransactions(prev => 
        prev.filter(t => !transactionsToConfirm.includes(t))
      );

      // If no transactions left, close the dialog
      if (extractedTransactions.length === transactionsToConfirm.length) {
        setShowConfirmationDialog(false);
      }

      toast({
        title: "Success",
        description: `Added ${transactionsToConfirm.length} transaction(s) successfully!`,
      });
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast({
        title: "Error",
        description: "Failed to add transaction",
        variant: "destructive",
      });
    }
  };

  const handleRecordAgain = () => {
    setShowConfirmationDialog(false);
    setExtractedTransactions([]);
    setCurrentTransactionIndex(0);
    setHasRecording(false);
    audioChunksRef.current = [];
  };

  const handleConfirmReceiptTransactions = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user || !selectedWallet) throw new Error('No user or wallet found');

      // Process all transactions
      for (const transaction of receiptTransactions) {
        const transactionAmount = transaction.amount;
        
        // Calculate new balance
        const newBalance = transaction.type === 'income' 
          ? selectedWallet.balance + transactionAmount
          : selectedWallet.balance - transactionAmount;
        
        console.log(`Receipt transaction: ${transaction.type}, Amount: ${transactionAmount}, Old balance: ${selectedWallet.balance}, New balance: ${newBalance}`);

        // Insert transaction
        const { error: transactionError } = await supabase
          .from('transactions')
          .insert({
            user_id: user.id,
            wallet_id: selectedWallet.id,
            amount: transactionAmount,
            category: transaction.category,
            transaction_date: new Date(transaction.date).toISOString(),
            description: transaction.description,
            account_type: accountType,
            type: transaction.type
          });

        if (transactionError) throw transactionError;

        // Update wallet balance directly instead of using RPC
        const { error: walletError } = await supabase
          .from('wallets')
          .update({ balance: newBalance })
          .eq('id', selectedWallet.id);

        if (walletError) throw walletError;
        
        // Update local wallet state to maintain consistency
        setSelectedWallet({
          ...selectedWallet,
          balance: newBalance
        });
      }

      toast({
        title: "Success",
        description: `Added ${receiptTransactions.length} transaction(s) successfully!`,
      });

      // Reset state
      setReceiptTransactions([]);
      setShowReceiptConfirmationDialog(false);
      setReceiptFile(null);
      
      // Reload wallets to update balances
      fetchWallets();
      onSuccess?.();
    } catch (error) {
      console.error('Error adding transactions:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to add transactions. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReceiptTransactions = () => {
    setReceiptTransactions([]);
    setShowReceiptConfirmationDialog(false);
    setReceiptFile(null);
  };

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Add Transaction</h2>
        </div>

        <div className="mb-6 space-y-2">
          <Label htmlFor="wallet">Select Wallet</Label>
          <Select 
            value={selectedWallet?.id || ''} 
            onValueChange={(value) => {
              const wallet = wallets.find(w => w.id === value);
              setSelectedWallet(wallet || null);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a wallet" />
            </SelectTrigger>
            <SelectContent>
              {wallets.map((wallet) => (
                <SelectItem key={wallet.id} value={wallet.id}>
                  {wallet.name} (Rp {wallet.balance.toLocaleString('id-ID')})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Tabs defaultValue="manual" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="manual">
              <Plus className="h-4 w-4 mr-2" />
              Manual
            </TabsTrigger>
            <TabsTrigger value="receipt">
              <Receipt className="h-4 w-4 mr-2" />
              Receipt
            </TabsTrigger>
            <TabsTrigger value="voice">
              <Mic className="h-4 w-4 mr-2" />
              Voice
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual">
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={type} onValueChange={(value: 'income' | 'expense') => setType(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="income">Income</SelectItem>
                      <SelectItem value="expense">Expense</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="Enter amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(date, "PPP")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(date) => date && setDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Adding...' : 'Add Transaction'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="receipt">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Transaction Type</Label>
                  <Select value={transactionType} onValueChange={(value: 'income' | 'expense') => setTransactionType(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="income">Income</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="receipt-upload"
                  disabled={processing || !selectedWallet}
                />
                <label
                  htmlFor="receipt-upload"
                  className={`cursor-pointer flex flex-col items-center ${!selectedWallet ? 'opacity-50' : ''}`}
                >
                  <Upload className="h-12 w-12 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    {!selectedWallet 
                      ? 'Please select a wallet first' 
                      : receiptFile 
                        ? receiptFile.name 
                        : 'Click to upload receipt'}
                  </p>
                </label>
              </div>
              <Button 
                className="w-full" 
                disabled={!receiptFile || processing || !selectedWallet}
                onClick={processReceipt}
              >
                {processing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Receipt className="h-4 w-4 mr-2" />
                    Process Receipt
                  </>
                )}
              </Button>
              {receiptTransactions.length > 0 && (
                <Button 
                  className="w-full" 
                  onClick={handleConfirmReceiptTransactions}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Receipt className="h-4 w-4 mr-2" />
                      Confirm {receiptTransactions.length} Transaction(s)
                    </>
                  )}
                </Button>
              )}
            </div>
          </TabsContent>

          <TabsContent value="voice">
            <div className="space-y-4">
              <div className="flex flex-col items-center space-y-4">
                <Button
                  type="button"
                  variant={isRecording ? "destructive" : "default"}
                  size="lg"
                  className="rounded-full w-16 h-16"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={processing || !selectedWallet}
                >
                  {processing ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <Mic className="h-6 w-6" />
                  )}
                </Button>
                <p className="text-center text-sm text-muted-foreground">
                  {!selectedWallet 
                    ? 'Please select a wallet first' 
                    : isRecording 
                      ? 'Recording... Click to stop' 
                      : 'Click to start recording'}
                </p>
                {hasRecording && !processing && (
                  <Button
                    type="button"
                    onClick={analyzeRecording}
                    className="w-full"
                    disabled={!selectedWallet}
                  >
                    <Mic className="h-4 w-4 mr-2" />
                    Analyze Recording
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </Card>

      <Dialog open={showConfirmationDialog} onOpenChange={setShowConfirmationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Transactions</DialogTitle>
            <DialogDescription>
              Please review the extracted transaction details before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {extractedTransactions.map((transaction, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium">Transaction {index + 1}</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Handle individual transaction confirmation
                      handleConfirmTransaction([transaction]);
                    }}
                    disabled={loading}
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label>Type</Label>
                    <p className="text-sm capitalize">{transaction.type}</p>
                  </div>
                  <div>
                    <Label>Amount</Label>
                    <p className="text-sm">Rp {transaction.amount.toLocaleString('id-ID')}</p>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <p className="text-sm">{transaction.category}</p>
                  </div>
                  <div>
                    <Label>Date</Label>
                    <p className="text-sm">
                      {transaction.date ? format(new Date(transaction.date), "PPP") : format(new Date(), "PPP")}
                    </p>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <p className="text-sm">{transaction.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleRecordAgain}>
              Record Again
            </Button>
            <Button onClick={() => handleConfirmTransaction(extractedTransactions)} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReceiptConfirmationDialog} onOpenChange={setShowReceiptConfirmationDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Receipt Transactions</DialogTitle>
            <DialogDescription>
              Please review the extracted transactions before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {receiptTransactions.map((transaction, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div>
                  <Label>Amount</Label>
                  <p className="text-sm">Rp {transaction.amount.toLocaleString('id-ID')}</p>
                </div>
                <div>
                  <Label>Category</Label>
                  <p className="text-sm">{transaction.category}</p>
                </div>
                <div>
                  <Label>Date</Label>
                  <p className="text-sm">
                    {transaction.date ? format(new Date(transaction.date), "PPP") : format(new Date(), "PPP")}
                  </p>
                </div>
                <div>
                  <Label>Description</Label>
                  <p className="text-sm">{transaction.description}</p>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCancelReceiptTransactions}>
              Cancel
            </Button>
            <Button onClick={handleConfirmReceiptTransactions} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                'Confirm All'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransactionInput; 